import { useEffect, useRef, useState, useCallback } from 'react';

const IDLE_MS = 25 * 60 * 1000;   // show warning after 25 min idle
const WARN_SECS = 5 * 60;          // 5-minute countdown before forced logout

export default function useAdminTimeout({ onExpire }) {
  const [showWarning, setShowWarning] = useState(false);
  const [countdown, setCountdown] = useState(WARN_SECS);
  const warningActive = useRef(false);
  const idleTimer = useRef(null);
  const countdownTimer = useRef(null);

  const startCountdown = useCallback(() => {
    warningActive.current = true;
    setShowWarning(true);
    setCountdown(WARN_SECS);
    countdownTimer.current = setInterval(() => {
      setCountdown(c => {
        if (c <= 1) {
          clearInterval(countdownTimer.current);
          onExpire();
          return 0;
        }
        return c - 1;
      });
    }, 1000);
  }, [onExpire]);

  const resetTimer = useCallback(() => {
    warningActive.current = false;
    setShowWarning(false);
    clearInterval(countdownTimer.current);
    setCountdown(WARN_SECS);
    clearTimeout(idleTimer.current);
    idleTimer.current = setTimeout(startCountdown, IDLE_MS);
  }, [startCountdown]);

  useEffect(() => {
    const events = ['mousemove', 'keydown', 'mousedown', 'touchstart', 'scroll'];
    const onActivity = () => {
      if (!warningActive.current) resetTimer();
    };
    events.forEach(e => window.addEventListener(e, onActivity, { passive: true }));
    idleTimer.current = setTimeout(startCountdown, IDLE_MS);
    return () => {
      events.forEach(e => window.removeEventListener(e, onActivity));
      clearTimeout(idleTimer.current);
      clearInterval(countdownTimer.current);
    };
  }, [resetTimer, startCountdown]);

  return { showWarning, countdown, resetTimer };
}
